/**
 * Календарные даты в фильтрах CRM (period_from / period_to и аналоги).
 *
 * Как сейчас:
 * - FilterDateParser.sortDate (dateParser.ts) при submit переводит select datepicker в *_from / *_to.
 * - Legacy: `new Date('yyyy-MM-dd')` + correctDate — сдвигает день в TZ западнее UTC (CRM-5433).
 * - Новый путь: normalizeDateRangeParams — локальный парс yyyy-MM-dd, без UTC-сдвига.
 * - Включение нового пути: safeLocalDateParse: true на Datepicker (сейчас только topOfWeek.ts / period).
 * - Остальные страницы с range-datepicker пока на legacy, поведение не менялось.
 *
 * Rollout (постепенно, без дублирования логики):
 * - На нужном Datepicker в TablePage/*.ts или inline-конфиге: safeLocalDateParse: true.
 *
 * FINAL MIGRATION (когда флаг стоит на всех datepicker с range):
 * - dateParser.ts: убрать if (filter.safeLocalDateParse), всегда вызывать normalizeDateRangeParams;
 *   удалить legacy-блок (new Date(fromDate) … correctDate) и метод correctDate.
 * - types/index.d.ts, base.filter.ts: удалить поле safeLocalDateParse.
 * - Все Datepicker: удалить строки safeLocalDateParse: true.
 * - Этот файл не удалять — в нём остаётся parseCalendarDateString / normalizeDateRangeParams.
 */

/** yyyy-MM-dd */
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
/** yyyy-MM */
const ISO_MONTH_RE = /^(\d{4})-(\d{2})$/;

export function parseCalendarDateString(value: string): Date {
    const dateMatch = ISO_DATE_RE.exec(value);
    if (dateMatch) {
        return new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
    }

    const monthMatch = ISO_MONTH_RE.exec(value);
    if (monthMatch) {
        return new Date(Number(monthMatch[1]), Number(monthMatch[2]) - 1, 1);
    }

    // Строка не в формате yyyy-MM-dd или yyyy-MM (например «2026-06-01 00:00:00»).
    // Топ за неделю отдаёт только yyyy-MM-dd — эта ветка для него не используется.
    // Если включать safeLocalDateParse на фильтры с временем — здесь нужен отдельный парс,
    // см. Datepicker created_at + isTimeAdder в services/TablePage/offers.ts.
    return new Date(value);
}

function formatCalendarBound(date: Date, type: 'start' | 'end', pickerType: 'date' | 'month'): string {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const rawDay = date.getDate();
    const day = `${rawDay < 10 ? '0' : ''}${rawDay}`;

    if (type === 'start') {
        const firstDay = pickerType === 'month' ? '01' : day;
        return `${year}-${month}-${firstDay}`;
    }

    const lastDay = pickerType === 'month'
        ? new Date(year, date.getMonth() + 1, 0).getDate()
        : day;
    return `${year}-${month}-${lastDay}`;
}

export function normalizeDateRangeParams(
    fromDate: string,
    toDate: string,
    pickerType: 'date' | 'month',
): { from: string; to: string } {
    const from = parseCalendarDateString(fromDate);
    const to = parseCalendarDateString(toDate);

    if (from.valueOf() > to.valueOf()) {
        return {
            from: formatCalendarBound(to, 'start', pickerType),
            to: formatCalendarBound(from, 'end', pickerType),
        };
    }

    return {
        from: formatCalendarBound(from, 'start', pickerType),
        to: formatCalendarBound(to, 'end', pickerType),
    };
}
